import { t } from '@lingui/macro';
import { useCallback } from 'react';

import { fetchJson } from '@api/ApiHelper';
import { ToastLooks, useCreateToast } from '@components/common/toast';

import { amplitudeService } from '../../../../services/amplitude';

export interface UpdateFaceNameParams {
  personId: string;
  name: string;
  old_name: string;
  /** When set, emits FieldEdit analytics after a successful save (People Hub list only). */
  analytics?: {
    faceCount: number;
    source: 'people_hub_list';
  };
}

interface UpdateFaceNameResponse {
  id: string;
  identity_name: string;
  source_url: string;
}

export const useUpdateFaceName = (
  onSuccess?: () => void
): { updateFaceName: (params: UpdateFaceNameParams) => Promise<boolean> } => {
  const { toast } = useCreateToast();

  const updateFaceName = useCallback(
    async ({ personId, name, old_name, analytics }: UpdateFaceNameParams): Promise<boolean> => {
      try {
        // Get brandfolder key from global BFG object
        const brandfolderKey = BFG.brandfolder_key;

        if (!brandfolderKey) {
          toast(t`Brandfolder not found`, ToastLooks.Error);
          return false;
        }

        if (!personId) {
          toast(t`Person ID is required`, ToastLooks.Error);
          return false;
        }

        // Make API call to update face name
        // Allow empty string to unname the person
        await fetchJson<UpdateFaceNameResponse>({
          url: `/api/v4/private/people_tagging/brandfolders/${brandfolderKey}/identities/${personId}`,
          method: 'PUT',
          body: {
            name: name.trim(),
            old_name: old_name?.trim() || '',
          },
        });

        if (
          analytics?.source === 'people_hub_list' &&
          typeof window !== 'undefined' &&
          window.BFG?.hasFeature?.('people_tagging') &&
          amplitudeService.isReady()
        ) {
          const oldTrim = (old_name ?? '').trim();
          const newTrim = name.trim();
          const eventArea = 'AppFrontend';
          const eventName = 'FieldEdit';
          const productArea = 'BulkManagement';
          const featureArea = 'PeopleHub';
          const elementLocation = 'PeopleHubPage';
          const source = 'people_hub_list';
          if (!oldTrim && newTrim) {
            const elementName = 'NameIdentity';
            const eventFull = `${eventArea}.${productArea}.${featureArea}.${elementName}.${eventName}`;
            amplitudeService.trackEvent(eventName, {
              eventArea,
              productArea,
              featureArea,
              elementLocation,
              elementName,
              eventName,
              eventFull,
              brandfolderId: window.BFG?.brandfolder_key ?? undefined,
              organizationId: window.BFG?.organization_key ?? undefined,
              userId: window.BFG?.currentUser?.user_id ?? undefined,
              userRole: window.BFG?.currentUser?.role ?? undefined,
              identityId: personId,
              source,
              faceCount: analytics.faceCount,
            });
          } else if (oldTrim && newTrim !== oldTrim) {
            const elementName = 'RenameIdentity';
            const eventFull = `${eventArea}.${productArea}.${featureArea}.${elementName}.${eventName}`;
            amplitudeService.trackEvent(eventName, {
              eventArea,
              productArea,
              featureArea,
              elementLocation,
              elementName,
              eventName,
              eventFull,
              brandfolderId: window.BFG?.brandfolder_key ?? undefined,
              organizationId: window.BFG?.organization_key ?? undefined,
              userId: window.BFG?.currentUser?.user_id ?? undefined,
              userRole: window.BFG?.currentUser?.role ?? undefined,
              identityId: personId,
              source,
            });
          }
        }

        // Call success callback if provided (e.g., to refetch data)
        onSuccess?.();

        return true;
      } catch (error) {
        toast(t`Failed to update person name. Please try again.`, ToastLooks.Error);
        return false;
      }
    },
    [onSuccess, toast]
  );

  return { updateFaceName };
};
